// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API module for updating customer information in the Hardfin system.
 * Provides type-safe customer record updates with Zod validation.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { customerSchema } from '@/customer/api/types';

/**
 * Schema for validating the response from the update customer API endpoint.
 * Includes both the updated customer data and metadata about the request.
 */
const updateCustomerResponseSchema = z
  .object({
    data: customerSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, customer: data }));

/**
 * Type definition for the update customer API response.
 * Contains the updated customer data and request metadata.
 */
type UpdateCustomerResponse = z.infer<typeof updateCustomerResponseSchema>;

/**
 * Parameters for updating a customer record.
 * All fields are optional, allowing partial updates.
 *
 * @property name - The display name of the customer
 * @property isArchived - Whether the customer record is archived
 * @property isCustomer - Whether this company has the customer role
 * @property isSupplier - Whether this company has the supplier role
 * @property billingAddress - The customer's billing address
 * @property billingContact - The customer's billing contact information
 * @property billingContact.name - Name of the billing contact person
 * @property billingContact.email - Email of the billing contact person
 * @property billingContact.phone - Phone number of the billing contact person
 * @property domain - The customer's domain for logo lookup (e.g., "acme.com")
 */
export interface UpdateCustomerParameters {
  name?: string;
  isArchived?: boolean;
  isCustomer?: boolean;
  isSupplier?: boolean;
  billingAddress?: string | null;
  comment?: string | null;
  externalId?: string | null;
  domain?: string | null;
  billingContact?: {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
  };
}

/**
 * Updates an existing customer record in the Hardfin system.
 * Supports partial updates - only provided fields will be modified.
 *
 * @param key - The unique identifier key of the customer to update
 * @param parameters - Object containing the fields to update
 * @param parameters.name - Optional new name for the customer
 * @param parameters.isArchived - Optional flag to archive/unarchive the customer
 * @param parameters.billingAddress - Optional new billing address
 * @param parameters.billingContact - Optional new billing contact information
 * @returns Promise resolving to the updated customer data and request metadata
 * @throws Will throw an error if the customer is not found, API request fails, or response validation fails
 */
export async function updateCustomer(
  key: string,
  parameters: UpdateCustomerParameters
): Promise<UpdateCustomerResponse> {
  const url = `/v1/customer/${key}`;

  // Helper function to check if an object has any non-undefined values
  const hasDefinedValues = (
    obj: Record<string, string | null | undefined> | undefined
  ): boolean => {
    return obj
      ? Object.values(obj).some((value) => value !== undefined)
      : false;
  };

  const body = {
    ...parameters,
    billingContact: hasDefinedValues(parameters.billingContact)
      ? parameters.billingContact
      : undefined,
  };

  const response = await api.patch(url, body);

  return parseResponse(updateCustomerResponseSchema, response.data, {
    schemaName: 'UpdateCustomer',
    endpoint: url,
  });
}
